import { MobileContainer } from '@/components/layout/MobileContainer';
import { ModuleHeader } from '@/components/module/ModuleHeader';
import { SceneRenderer } from '@/components/scenes/SceneRenderer';
import { Switch } from '@/components/ui/switch';
import { getModule, getModuleScenes } from '@/data/moduleData';
import { useGameStore } from '@/store/useGameStore';
import { AnimatePresence, motion } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useLangNavigate, useLocalized, useT } from '@/i18n';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

export default function ModuleScreen() {
  const { moduleId } = useParams();
  const navigate = useLangNavigate();
  const t = useT();
  const {
    currentModuleId,
    currentSceneIndex,
    startModule,
    makeChoice,
    saveChecklistResult,
    nextScene,
    completeModule,
    voiceEnabled,
    toggleVoice,
  } = useGameStore();

  const module = useLocalized(getModule(moduleId));
  // ссылка обязана быть стабильной: ChoiceScene тасует варианты в useMemo по
  // scene.choices, и новый массив на каждый рендер тасовал бы их заново
  const rawScenes = useMemo(() => getModuleScenes(moduleId), [moduleId]);
  const scenes = useLocalized(rawScenes);

  useEffect(() => {
    if (moduleId && currentModuleId !== moduleId) {
      startModule(moduleId);
    }
  }, [moduleId, currentModuleId, startModule]);

  const currentScene = scenes[currentSceneIndex];

  const handleChoice = useCallback((sceneId, choice) => {
    makeChoice(sceneId, choice);
  }, [makeChoice]);

  const handleChecklistComplete = useCallback((sceneId, checkedItems, totalRequired) => {
    saveChecklistResult(sceneId, checkedItems, totalRequired);
  }, [saveChecklistResult]);

  const handleContinue = useCallback(() => {
    if (currentScene?.is_final || currentSceneIndex >= scenes.length - 1) {
      completeModule();
      navigate(`/results/${moduleId}`);
    } else {
      nextScene();
    }
  }, [currentScene, currentSceneIndex, scenes.length, completeModule, nextScene, navigate, moduleId]);

  const handleBack = () => {
    if (window.confirm(t('ui.module.leaveConfirm'))) {
      navigate('/home');
    }
  };

  if (!module || !scenes.length) {
    return (
      <MobileContainer>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground font-body">{t('ui.module.notFound')}</p>
        </div>
      </MobileContainer>
    );
  }

  return (
    <MobileContainer noPadding>
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Module header with progress */}
        <ModuleHeader
          title={t('ui.module.title', { title: module.title })}
          currentStep={currentSceneIndex}
          totalSteps={scenes.length}
          duration={module.duration_minutes}
          onBack={handleBack}
        />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto scrollbar-hide flex flex-col">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSceneIndex}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col"
            >
              <SceneRenderer
                scene={currentScene}
                onChoice={handleChoice}
                onChecklistComplete={handleChecklistComplete}
                onContinue={handleContinue}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Voice toggle footer */}
        <div className="bg-card border-t border-border px-4 py-2.5 flex items-center justify-center gap-2">
          {voiceEnabled ? <Volume2 size={16} className="text-primary" /> : <VolumeX size={16} className="text-muted-foreground" />}
          <span className="text-xs text-muted-foreground font-body">{voiceEnabled ? t('ui.module.voiceOn') : t('ui.module.voiceOff')}</span>
          <Switch
            checked={voiceEnabled}
            onCheckedChange={(checked) => {
              if (checked) {
                toast.info(t('ui.module.voiceUnavailable'), {
                  className: 'font-body text-xs',
                  duration: 2500,
                });
                return;
              }
              toggleVoice();
            }}
            className="scale-75"
          />
        </div>
      </div>
    </MobileContainer>
  );
}
